import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FileSpreadsheet,
  Calendar,
  Loader2,
  BarChart3,
  Table2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDre } from "@/hooks/use-dre";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { useAccountGroups } from "@/hooks/use-account-groups";
import { DreTable } from "@/components/dre/DreTable";
import { DreDashboard } from "@/components/dre/DreDashboard";
import { exportDreToPDF, exportDreToCSV } from "@/lib/export-utils";
import { toast } from "@/hooks/use-toast";

const monthLabels = [
  "Janeiro",
  "Fevereiro",
  "Marco",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const quarterLabels = [
  "1o Trimestre",
  "2o Trimestre",
  "3o Trimestre",
  "4o Trimestre",
];

const Dre = () => {
  const currentYear = new Date().getFullYear();

  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [year, setYear] = useState(currentYear);
  const [periodType, setPeriodType] = useState<
    "mensal" | "trimestral" | "anual"
  >("mensal");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  );
  const [selectedQuarter, setSelectedQuarter] = useState(
    Math.ceil((new Date().getMonth() + 1) / 3)
  );

  // Derive hook params from period type
  const month = periodType === "mensal" ? selectedMonth : null;
  const quarter = periodType === "trimestral" ? selectedQuarter : null;

  const dre = useDre(selectedGroup, year, month, quarter);
  const { groups } = useAccountGroups();

  // Build period label for exports
  const getPeriodLabel = (): string => {
    if (periodType === "mensal") {
      return `${monthLabels[selectedMonth - 1]} ${year}`;
    }
    if (periodType === "trimestral") {
      return `${quarterLabels[selectedQuarter - 1]} ${year}`;
    }
    return `Anual ${year}`;
  };

  const handleExportCSV = () => {
    if (dre.sections.length === 0) {
      toast({ title: "Nenhum dado para exportar", variant: "destructive" });
      return;
    }
    exportDreToCSV(dre.sections, dre.totals, getPeriodLabel());
    toast({ title: "CSV exportado com sucesso!" });
  };

  const handleExportPDF = () => {
    if (dre.sections.length === 0) {
      toast({ title: "Nenhum dado para exportar", variant: "destructive" });
      return;
    }
    exportDreToPDF(dre.sections, dre.totals, getPeriodLabel());
    toast({ title: "PDF exportado com sucesso!" });
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setYear((y) => y - 1);
    } else {
      setSelectedMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setYear((y) => y + 1);
    } else {
      setSelectedMonth((m) => m + 1);
    }
  };

  const handleGroupChange = (value: string) => {
    setSelectedGroup(value === "all" ? null : value);
  };

  // Year range: current year +/- 2
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  if (dre.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <FeatureGate feature="dre_reports">
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            <span className="sm:hidden">DRE</span>
            <span className="hidden sm:inline">DRE - Demonstrativo de Resultados</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Analise a estrutura de receitas, custos e despesas
          </p>
        </div>

        {/* Export Buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="default" size="sm" onClick={handleExportPDF}>
            <FileText className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
        {/* Account Group Selector */}
        <Select
          value={selectedGroup || "all"}
          onValueChange={handleGroupChange}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Todas as contas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as contas</SelectItem>
            {(groups || []).map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Year Selector */}
        <Select
          value={String(year)}
          onValueChange={(v) => setYear(Number(v))}
        >
          <SelectTrigger className="w-full sm:w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Period Type Tabs */}
        <Tabs
          value={periodType}
          onValueChange={(v) => setPeriodType(v as "mensal" | "trimestral" | "anual")}
          className="w-full sm:w-auto"
        >
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="mensal" className="flex-1 sm:flex-none">Mensal</TabsTrigger>
            <TabsTrigger value="trimestral" className="flex-1 sm:flex-none">Trimestral</TabsTrigger>
            <TabsTrigger value="anual" className="flex-1 sm:flex-none">Anual</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Period Sub-selector */}
      <div className="flex items-center gap-4">
        {periodType === "mensal" && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg min-w-0 flex-1 sm:min-w-[180px] sm:flex-none justify-center">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">
                {monthLabels[selectedMonth - 1]} {year}
              </span>
            </div>
            <Button variant="outline" size="icon" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {periodType === "trimestral" && (
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((q) => (
              <Button
                key={q}
                variant={selectedQuarter === q ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedQuarter(q)}
              >
                Q{q}
              </Button>
            ))}
          </div>
        )}

        {periodType === "anual" && (
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Ano {year}</span>
          </div>
        )}
      </div>

      {/* View Tabs */}
      <Tabs defaultValue="tabela" className="w-full">
        <TabsList>
          <TabsTrigger value="tabela">
            <Table2 className="h-4 w-4 mr-2" />
            Tabela DRE
          </TabsTrigger>
          <TabsTrigger value="dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tabela">
          <DreTable sections={dre.sections} totals={dre.totals} />
        </TabsContent>
        <TabsContent value="dashboard">
          <DreDashboard
            totals={dre.totals}
            comparison={dre.comparison}
            monthlyBreakdown={dre.monthly_breakdown}
            sections={dre.sections}
          />
        </TabsContent>
      </Tabs>
    </div>
    </FeatureGate>
  );
};

export default Dre;
